import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Select from '@radix-ui/react-select';
import { Check, ChevronDown, UserMinus, UserPlus, AlertCircle, X } from 'lucide-react';
import { membersApi, type MemberRole } from '../api/members';
import { usersApi } from '../api/users';
import { toast } from '../components/Toast';
import { formatRelativeTime, getCollaboratorColor, getInitials } from '../lib/utils';

const ROLE_LABEL: Record<MemberRole, string> = {
  admin: '管理员',
  editor: '编辑者',
  viewer: '只读',
};

const ROLE_TAG_CLASS: Record<MemberRole, string> = {
  admin: 'tag-accent',
  editor: 'tag-default',
  viewer: 'tag-default',
};

export function MembersPage() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const queryClient = useQueryClient();

  const { data: members, isLoading, error } = useQuery({
    queryKey: ['members', wsId],
    queryFn: () => membersApi.list(wsId),
    enabled: !!wsId,
  });

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: usersApi.me,
  });

  // 当前用户角色（用于判断是否能管理）
  const currentMember = members?.find((m) => m.id === me?.id);
  const canManage = currentMember?.role === 'admin';

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<MemberRole>('editor');
  const [inviteError, setInviteError] = useState<string | null>(null);

  const [removeTarget, setRemoveTarget] = useState<{ id: string; name: string } | null>(null);

  const inviteMutation = useMutation({
    mutationFn: () => membersApi.invite(wsId, { email: inviteEmail.trim(), role: inviteRole }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members', wsId] });
      toast.success(`邀请已发送至 ${inviteEmail}`);
      setInviteOpen(false);
      setInviteEmail('');
      setInviteRole('editor');
      setInviteError(null);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : '邀请失败';
      setInviteError(msg);
      toast.error(msg, '邀请失败');
    },
  });

  const roleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: MemberRole }) =>
      membersApi.updateRole(wsId, userId, role),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['members', wsId] });
      toast.success(`已修改角色为 ${ROLE_LABEL[vars.role]}`);
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : '修改角色失败');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => membersApi.remove(wsId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members', wsId] });
      toast.success('成员已移除');
      setRemoveTarget(null);
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : '移除失败');
    },
  });

  function handleInviteSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail.trim())) {
      setInviteError('邮箱格式不正确');
      return;
    }
    inviteMutation.mutate();
  }

  function handleRoleChange(userId: string, role: MemberRole) {
    if (userId === me?.id) {
      toast.warning('不能修改自己的角色');
      return;
    }
    roleMutation.mutate({ userId, role });
  }

  return (
    <div className="mx-auto max-w-4xl px-8 py-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">成员管理</h1>
          <p className="mt-1 text-sm text-text-secondary">
            {members ? `共 ${members.length} 位成员` : '加载中…'}
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            className="btn btn-primary btn-md"
            onClick={() => setInviteOpen(true)}
          >
            <UserPlus className="h-4 w-4" /> 邀请成员
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="mt-6 space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 animate-shimmer rounded-lg bg-bg-subtle" />
          ))}
        </div>
      ) : error ? (
        <div className="mt-6 flex flex-col items-center rounded-lg border border-border bg-bg-surface p-8 text-center">
          <AlertCircle className="h-8 w-8 text-danger" />
          <p className="mt-3 text-sm text-text-secondary">
            {error instanceof Error ? error.message : '加载失败'}
          </p>
          <button
            type="button"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['members', wsId] })}
            className="btn btn-secondary btn-md mt-4"
          >
            重试
          </button>
        </div>
      ) : members && members.length > 0 ? (
        <div className="mt-6 card divide-y divide-border">
          {members.map((m) => {
            const isMe = m.id === me?.id;
            const displayName = m.nickname || m.email.split('@')[0];
            return (
              <div key={m.id} className="flex items-center gap-4 p-4">
                <span
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-medium text-white"
                  style={{ backgroundColor: getCollaboratorColor(m.id) }}
                  aria-hidden
                >
                  {getInitials(displayName)}
                </span>
                <div className="flex-1 overflow-hidden">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{displayName}</span>
                    {isMe && <span className="tag tag-default">我</span>}
                  </div>
                  <div className="text-xs text-text-tertiary">{m.email}</div>
                  <div className="text-xs text-text-tertiary">
                    加入于 {formatRelativeTime(m.joinedAt)}
                  </div>
                </div>

                {/* 角色选择 */}
                {canManage && !isMe ? (
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger asChild>
                      <button
                        type="button"
                        className={`tag ${ROLE_TAG_CLASS[m.role]} cursor-pointer hover:opacity-80`}
                      >
                        {ROLE_LABEL[m.role]}
                        <ChevronDown className="h-3 w-3" />
                      </button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                      <DropdownMenu.Content
                        className="z-50 min-w-[140px] rounded-md border border-border bg-bg-surface p-1 shadow-md"
                        sideOffset={4}
                      >
                        {(['admin', 'editor', 'viewer'] as MemberRole[]).map((r) => (
                          <DropdownMenu.Item
                            key={r}
                            onSelect={() => handleRoleChange(m.id, r)}
                            className="flex cursor-pointer items-center justify-between rounded px-2 py-1.5 text-sm text-text-primary outline-none data-[highlighted]:bg-bg-subtle"
                          >
                            {ROLE_LABEL[r]}
                            {m.role === r && <Check className="h-3.5 w-3.5 text-accent" />}
                          </DropdownMenu.Item>
                        ))}
                      </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Root>
                ) : (
                  <span className={`tag ${ROLE_TAG_CLASS[m.role]}`}>{ROLE_LABEL[m.role]}</span>
                )}

                {/* 移除按钮 */}
                {canManage && !isMe && (
                  <button
                    type="button"
                    className="flex h-8 w-8 items-center justify-center rounded text-text-tertiary hover:bg-[#fef2f2] hover:text-danger"
                    onClick={() => setRemoveTarget({ id: m.id, name: displayName })}
                    aria-label={`移除 ${displayName}`}
                  >
                    <UserMinus className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mt-12 flex flex-col items-center text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-accent-50 text-accent">
            <UserPlus className="h-9 w-9" />
          </div>
          <p className="mt-4 text-base font-medium">还没有其他成员</p>
          <p className="mt-1 text-sm text-text-secondary">邀请团队成员加入空间，开始协作</p>
        </div>
      )}

      {/* 邀请弹窗 */}
      <Dialog.Root open={inviteOpen} onOpenChange={setInviteOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-bg-overlay animate-fade-in" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-[480px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-bg-surface p-6 shadow-xl"
            aria-describedby={undefined}
          >
            <Dialog.Title className="text-lg font-semibold">邀请成员</Dialog.Title>
            <Dialog.Description className="mt-1 text-sm text-text-secondary">
              通过邮箱邀请，对方将收到一封邀请邮件。
            </Dialog.Description>
            <form onSubmit={handleInviteSubmit} className="mt-5 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="invite-email">
                  邮箱 <span className="text-danger">*</span>
                </label>
                <input
                  id="invite-email"
                  type="email"
                  required
                  autoFocus
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="input"
                  placeholder="bob@example.com"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">角色</label>
                <Select.Root
                  value={inviteRole}
                  onValueChange={(v) => setInviteRole(v as MemberRole)}
                >
                  <Select.Trigger className="input flex items-center justify-between">
                    <Select.Value>{ROLE_LABEL[inviteRole]}</Select.Value>
                    <Select.Icon>
                      <ChevronDown className="h-3.5 w-3.5 text-text-tertiary" />
                    </Select.Icon>
                  </Select.Trigger>
                  <Select.Portal>
                    <Select.Content
                      className="z-50 min-w-[160px] overflow-hidden rounded-md border border-border bg-bg-surface shadow-md"
                      position="popper"
                      sideOffset={4}
                    >
                      <Select.Viewport className="p-1">
                        {(['admin', 'editor', 'viewer'] as MemberRole[]).map((r) => (
                          <Select.Item
                            key={r}
                            value={r}
                            className="flex cursor-pointer items-center justify-between rounded px-2 py-1.5 text-sm text-text-primary outline-none data-[highlighted]:bg-bg-subtle"
                          >
                            <Select.ItemText>{ROLE_LABEL[r]}</Select.ItemText>
                            <Select.ItemIndicator>
                              <Check className="h-3.5 w-3.5 text-accent" />
                            </Select.ItemIndicator>
                          </Select.Item>
                        ))}
                      </Select.Viewport>
                    </Select.Content>
                  </Select.Portal>
                </Select.Root>
              </div>

              {inviteError && (
                <div className="flex items-center gap-2 rounded-md bg-[#fef2f2] px-3 py-2 text-sm text-danger">
                  <AlertCircle className="h-4 w-4" />
                  {inviteError}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setInviteOpen(false)}
                  className="btn btn-ghost btn-md"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={inviteMutation.isPending}
                  className="btn btn-primary btn-md"
                >
                  {inviteMutation.isPending ? '发送中…' : '发送邀请'}
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* 移除确认弹窗 */}
      <Dialog.Root open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-bg-overlay animate-fade-in" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-[420px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-bg-surface p-6 shadow-xl"
            aria-describedby={undefined}
          >
            <div className="flex items-start justify-between">
              <Dialog.Title className="text-lg font-semibold">移除成员</Dialog.Title>
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="flex h-6 w-6 items-center justify-center rounded text-text-tertiary hover:bg-bg-subtle"
                  aria-label="关闭"
                >
                  <X className="h-4 w-4" />
                </button>
              </Dialog.Close>
            </div>
            <Dialog.Description className="mt-2 text-sm text-text-secondary">
              确定要将 <span className="font-medium text-text-primary">{removeTarget?.name}</span> 移出该空间吗？该成员将无法再访问空间内的所有内容。
            </Dialog.Description>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setRemoveTarget(null)}
                className="btn btn-ghost btn-md"
              >
                取消
              </button>
              <button
                type="button"
                disabled={removeMutation.isPending}
                onClick={() => removeTarget && removeMutation.mutate(removeTarget.id)}
                className="btn btn-danger btn-md"
              >
                {removeMutation.isPending ? '移除中…' : '移除'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
